import { initializeApp } from 'firebase/app';
import {
    getAuth,
    signInAnonymously,
    signInWithCustomToken,
    signOut,
    onAuthStateChanged,
    UserCredential
} from 'firebase/auth';
import { getDatabase, ref, set, get, update } from 'firebase/database';
import FirebaseService from './FirebaseService';
import ApiService from './ApiService';

// Firebase configuration (same as frontend)
const firebaseConfig = {
    apiKey: "AIzaSyAI7ps1aCmABNpQdcQst4KA7lharZxheys",
    authDomain: "com-wearit-app.firebaseapp.com",
    databaseURL: "https://com-wearit-app-default-rtdb.firebaseio.com",
    projectId: "com-wearit-app",
    storageBucket: "com-wearit-app.firebasestorage.app",
    messagingSenderId: "181542753360",
    appId: "1:181542753360:web:01751095c2a3b2cd012d55"
};

// Initialize Firebase for dashboard auth
let app;
let auth;
let database;

try {
    app = initializeApp(firebaseConfig, 'dashboardAuthApp');
    auth = getAuth(app);
    database = getDatabase(app);
} catch (error) {
    // Firebase already initialized, try with fallback name
    try {
        app = initializeApp(firebaseConfig, `dashboardAuth_${Date.now()}`);
        auth = getAuth(app);
        database = getDatabase(app);
    } catch (fallbackError) {
        console.log('Failed to initialize Firebase for dashboard auth:', fallbackError);
        auth = null;
        database = null;
    }
}

class FirebaseAuthService {
    constructor() {
        this.isInitialized = false;
        this.currentUser = null;
        this.authStateListeners = [];
    }

    /**
     * Initialize Firebase authentication for dashboard
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        try {
            if (!auth || !database) {
                console.log('❌ [FIREBASE AUTH] Firebase not properly initialized');
                return false;
            }

            // Set up auth state listener
            this.setupAuthStateListener();

            // Try to get current user or authenticate anonymously
            const isAuthenticated = await this.ensureAuthentication();

            this.isInitialized = true;
            // Firebase auth initialized
            return isAuthenticated;
        } catch (error) {
            console.log('❌ [FIREBASE AUTH] Error initializing Firebase auth:', error);
            this.isInitialized = true;
            return false;
        }
    }

    /**
     * Set up Firebase auth state listener
     */
    setupAuthStateListener() {
        if (!auth) return;

        onAuthStateChanged(auth, (user) => {
            this.currentUser = user;

            // Notify all listeners
            this.authStateListeners.forEach(callback => {
                try {
                    callback(user);
                } catch (error) {
                    // Silent error handling
                }
            });
        });
    }

    /**
     * Add auth state listener
     * @param {Function} callback - Callback function
     * @returns {Function} Unsubscribe function
     */
    onAuthStateChanged(callback) {
        this.authStateListeners.push(callback);

        // Return unsubscribe function
        return () => {
            const index = this.authStateListeners.indexOf(callback);
            if (index > -1) {
                this.authStateListeners.splice(index, 1);
            }
        };
    }

    /**
     * Get current Firebase user ID
     * @returns {string|null} Firebase user ID
     */
    getCurrentFirebaseUid() {
        return auth?.currentUser?.uid || null;
    }

    /**
     * Check if user is authenticated with Firebase
     * @returns {boolean} Authentication status
     */
    isAuthenticated() {
        return auth?.currentUser != null;
    }

    /**
     * Ensure Firebase authentication is established
     * @returns {Promise<boolean>} True if authentication is successful
     */
    async ensureAuthentication() {
        try {
            // Already signed in with a real (non-anonymous) identity
            if (auth.currentUser && !auth.currentUser.isAnonymous) {
                return true;
            }

            // Preferred: sign in with a backend-minted custom token so the
            // dashboard carries an admin/support role claim. RTDB rules gate
            // liveSupport (and chat) access on auth.token.role, which
            // anonymous auth can never satisfy.
            const signedInWithRole = await this.signInWithAdminRole();
            if (signedInWithRole) {
                return true;
            }

            // Already anonymous from a previous attempt — still usable for
            // paths that only require auth != null.
            if (auth.currentUser) {
                return true;
            }

            // Fallback: anonymous auth (limited access; no support-chat visibility)
            const userCredential = await signInAnonymously(auth);
            return !!userCredential.user;
        } catch (error) {
            console.log('❌ [FIREBASE AUTH] Authentication failed:', error);
            return false;
        }
    }

    /**
     * Sign in to Firebase with a custom token minted by the backend that
     * carries the admin/support role claim. Returns false (without throwing)
     * when the token can't be obtained, so callers can fall back to anonymous.
     * @returns {Promise<boolean>} True if custom-token sign-in succeeded
     */
    async signInWithAdminRole() {
        try {
            const resp = await ApiService.fetchData({
                url: '/firebase/admin-token',
                method: 'get',
            });
            const token = resp?.data?.data?.token;
            if (!token) {
                return false;
            }
            await signInWithCustomToken(auth, token);
            return true;
        } catch (error) {
            console.log(
                '⚠️ [FIREBASE AUTH] Custom-token sign-in unavailable, falling back:',
                error?.message || error
            );
            return false;
        }
    }

    /**
     * Get Firebase UID with fallback to anonymous auth
     * @returns {Promise<string|null>} Firebase UID
     */
    async getFirebaseUidWithFallback() {
        // Already signed in with a real (non-anonymous) identity — reuse it.
        if (auth?.currentUser && !auth.currentUser.isAnonymous) {
            return auth.currentUser.uid;
        }

        // Prefer a real admin/support identity carrying an auth.token.role
        // claim. The RTDB rules gate writes to /users, and reads of
        // liveSupport/globalPresence, on that claim — anonymous auth can never
        // satisfy them, which is what causes the permission_denied errors.
        try {
            const signedInWithRole = await this.signInWithAdminRole();
            if (signedInWithRole && auth?.currentUser?.uid != null) {
                return auth.currentUser.uid;
            }
        } catch (e) {
            console.log('⚠️ [FIREBASE AUTH] Admin-role sign-in failed, falling back to anonymous:', e?.message || e);
        }

        // Reuse an existing (anonymous) session if one is already present.
        let firebaseUid = auth?.currentUser?.uid;
        if (firebaseUid != null) {
            return firebaseUid;
        }

        // Last resort: anonymous authentication (limited access).
        try {
            const userCredential = await signInAnonymously(auth);
            firebaseUid = userCredential.user?.uid;
            if (firebaseUid != null) {
                return firebaseUid;
            }
        } catch (e) {
            console.log('❌ [FIREBASE AUTH] Anonymous auth failed:', e);
        }

        return null;
    }

    /**
     * Initialize Firebase authentication for dashboard user
     * @param {string} appUserId - App user ID (admin/support user)
     * @param {string} userName - User name
     * @param {string} userType - Type of user ('admin', 'support')
     * @param {Object} userInfo - Additional user information
     * @returns {Promise<string|null>} Firebase UID or admin key
     */
    async initializeForDashboardUser(appUserId, userName = null, userType = 'admin', userInfo = {}) {
        try {
            // Initializing for dashboard user

            // Ensure Firebase authentication
            const firebaseUid = await this.getFirebaseUidWithFallback();
            if (!firebaseUid) {
                console.log('❌ [FIREBASE AUTH] Failed to get Firebase UID');
                return null;
            }

            // For dashboard users, use Firebase's native users collection
            const adminKey = userType === 'support' ? 'support' : appUserId;

            const adminData = {
                id: adminKey,
                name: userName || userInfo.name || `${userType.charAt(0).toUpperCase() + userType.slice(1)} User`,
                email: userInfo.email || `${adminKey}@wearit.me`,
                role: userType,
                avatar: userInfo.avatar || '',
                description: userInfo.description || `Official WearIt ${userType.charAt(0).toUpperCase() + userType.slice(1)} Team`,
                isOnline: true,
                lastSeen: Date.now(),
                createdAt: Date.now(),
                firebaseUid: firebaseUid
            };

            // Store in Firebase's users collection
            await set(ref(database, `users/${adminKey}`), adminData);

            // Create user mapping for consistency
            await this.storeUserMapping(appUserId, userType, userInfo, firebaseUid);

            // Dashboard user initialized in Firebase
            return adminKey; // Return admin key as identifier
        } catch (error) {
            console.log('❌ [FIREBASE AUTH] Error initializing dashboard user:', error);
            return null;
        }
    }

    /**
     * Store Firebase UID to app user ID mapping
     * @param {string} appUserId - App user ID
     * @param {string} userType - Type of user ('admin', 'support')
     * @param {Object} userInfo - Additional user information
     * @param {string} firebaseUid - Firebase UID
     * @returns {Promise<void>}
     */
    async storeUserMapping(appUserId, userType = 'admin', userInfo = {}, firebaseUid = null) {
        try {
            const uid = firebaseUid || auth.currentUser?.uid;
            if (uid != null) {
                const mappingData = {
                    appUserId: appUserId,
                    firebaseUid: uid,
                    userType: userType,
                    userInfo: {
                        name: userInfo.name || '',
                        email: userInfo.email || '',
                        avatar: userInfo.avatar || '',
                        role: userInfo.role || userType
                    },
                    timestamp: Date.now(),
                    platform: 'dashboard'
                };

                await set(ref(database, `userMappings/${uid}`), mappingData);
                // User mapping stored
            }
        } catch (e) {
            console.log('❌ [FIREBASE AUTH] Error storing user mapping:', e);
        }
    }

    /**
     * Get app user ID from Firebase UID
     * @param {string} firebaseUid - Firebase user ID
     * @returns {Promise<string|null>} App user ID
     */
    async getAppUserIdFromFirebaseUid(firebaseUid) {
        try {
            const snapshot = await get(ref(database, `userMappings/${firebaseUid}`));
            if (snapshot.exists()) {
                const data = snapshot.val();
                return data.appUserId;
            }
        } catch (e) {
            console.log('❌ [FIREBASE AUTH] Error getting app user ID:', e);
        }
        return null;
    }

    /**
     * Get Firebase UID from app user ID
     * @param {string} appUserId - App user ID
     * @returns {Promise<string|null>} Firebase user ID
     */
    async getFirebaseUidFromAppUserId(appUserId) {
        try {
            const snapshot = await get(ref(database, 'userMappings'));
            if (snapshot.exists()) {
                const data = snapshot.val();
                for (const [firebaseUid, mapping] of Object.entries(data)) {
                    if (mapping.appUserId === appUserId) {
                        return firebaseUid;
                    }
                }
            }
        } catch (e) {
            console.log('❌ [FIREBASE AUTH] Error getting Firebase UID:', e);
        }
        return null;
    }

    /**
     * Update user online status in Firebase
     * @param {string} appUserId - App user ID
     * @param {boolean} isOnline - Online status
     * @param {string} userType - User type
     * @returns {Promise<boolean>} Success status
     */
    async updateUserOnlineStatus(appUserId, isOnline, userType = 'admin') {
        try {
            const adminKey = userType === 'support' ? 'support' : appUserId;
            const userRef = ref(database, `users/${adminKey}`);

            await update(userRef, {
                isOnline: isOnline,
                lastSeen: Date.now()
            });

            // User online status updated
            return true;
        } catch (error) {
            console.log('❌ [FIREBASE AUTH] Error updating user online status:', error);
            return false;
        }
    }

    /**
     * Sign out from Firebase
     * @returns {Promise<void>}
     */
    async signOut() {
        try {
            if (auth) {
                await signOut(auth);
                // Signed out from Firebase
            }
        } catch (error) {
            console.log('❌ [FIREBASE AUTH] Error signing out:', error);
            throw error;
        }
    }

    /**
     * Get Firebase auth status
     * @returns {Object} Auth status information
     */
    getAuthStatus() {
        return {
            isInitialized: this.isInitialized,
            isAuthenticated: this.isAuthenticated(),
            currentUser: this.currentUser,
            firebaseUid: this.getCurrentFirebaseUid(),
            auth: !!auth,
            database: !!database
        };
    }
}

// Create singleton instance
const firebaseAuthService = new FirebaseAuthService();

// Export the shared database instance for other services to use
export { database as firebaseDatabase, auth as firebaseAuth };

// Don't auto-initialize - let components control when to initialize

export default firebaseAuthService;


